import type { RefObject } from 'react';
import { useCallback, useEffect, useState } from 'react';

export const usePictureInPicture = (videoRef: RefObject<HTMLVideoElement>) => {
  const [isPictureInPicture, setIsPictureInPicture] = useState(false);
  const [isPictureInPictureEnabled, setIsPictureInPictureEnabled] =
    useState(false);

  const enterPictureInPicture = useCallback(async () => {
    await videoRef.current?.requestPictureInPicture();
  }, [videoRef]);

  const exitPictureInPicture = useCallback(async () => {
    await document.exitPictureInPicture();
  }, []);

  const togglePictureInPicture = useCallback(() => {
    if (isPictureInPicture) {
      return exitPictureInPicture();
    } else {
      return enterPictureInPicture();
    }
  }, [isPictureInPicture, exitPictureInPicture, enterPictureInPicture]);

  useEffect(() => {
    setIsPictureInPictureEnabled(document.pictureInPictureEnabled);

    const handlePictureInPictureChange = () => {
      const video = videoRef.current;
      const pictureInPictureElement = document.pictureInPictureElement;
      const isVideoInPicture = pictureInPictureElement === video;
      setIsPictureInPicture(
        !!video && !!pictureInPictureElement && isVideoInPicture,
      );
    };

    document.addEventListener(
      'enterpictureinpicture',
      handlePictureInPictureChange,
    );
    document.addEventListener(
      'leavepictureinpicture',
      handlePictureInPictureChange,
    );
    return () => {
      document.removeEventListener(
        'enterpictureinpicture',
        handlePictureInPictureChange,
      );
      document.removeEventListener(
        'leavepictureinpicture',
        handlePictureInPictureChange,
      );
    };
  }, [videoRef]);

  return {
    isPictureInPicture,
    isPictureInPictureEnabled,
    togglePictureInPicture,
    enterPictureInPicture,
    exitPictureInPicture,
  };
};
